import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, Plus, Trash, Receipt, Wallet, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const METHOD_LABEL = { especes: "Espèces", virement: "Virement", mobile_money: "Mobile Money", cheque: "Chèque", autre: "Autre" };

export default function Finance() {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filiereFilter, setFiliereFilter] = useState("all");
  const [detailStudentId, setDetailStudentId] = useState(null);
  const [newPlanOpen, setNewPlanOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/finance/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filieres = useMemo(() => [...new Set(overview.map((s) => s.filiere).filter(Boolean))].sort(), [overview]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return overview.filter((s) => {
      if (filiereFilter !== "all" && s.filiere !== filiereFilter) return false;
      if (q && !s.student_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [overview, search, filiereFilter]);

  const statusOf = (s) => {
    if (s.late_count > 0) return { label: `${s.late_count} échéance(s) en retard`, color: "bg-accent hover:bg-accent" };
    if (s.total_remaining > 0) return { label: "À jour", color: "bg-secondary text-foreground hover:bg-secondary" };
    return { label: "Soldé", color: "bg-emerald-600 hover:bg-emerald-600" };
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Finance & Scolarité</h1>
          <p className="text-sm text-muted-foreground">Échéanciers, paiements et retards par étudiant.</p>
        </div>
        <NewPlanDialog open={newPlanOpen} setOpen={setNewPlanOpen} onCreated={(id) => { load(); setDetailStudentId(id); }} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un étudiant..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="finance-search" />
        </div>
        {filieres.length > 0 && (
          <Select value={filiereFilter} onValueChange={setFiliereFilter}>
            <SelectTrigger className="w-48" data-testid="finance-filiere-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les filières</SelectItem>
              {filieres.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {!loading && filtered.length === 0 ? (
        <EmptyState icon={Wallet} title="Aucun échéancier" description="Créez le premier échéancier de scolarité pour un étudiant." actionLabel="Nouvel échéancier" onAction={() => setNewPlanOpen(true)} />
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Étudiant</TableHead>
                <TableHead>Filière</TableHead>
                <TableHead>Année</TableHead>
                <TableHead className="text-right">Dû</TableHead>
                <TableHead className="text-right">Payé</TableHead>
                <TableHead className="text-right">Restant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const st = statusOf(s);
                return (
                  <TableRow key={s.student_id} data-testid={`finance-row-${s.student_id}`}>
                    <TableCell className="font-medium">{s.student_name}</TableCell>
                    <TableCell className="text-muted-foreground">{s.filiere || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.academic_year || "—"}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{s.total_due.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{s.total_paid.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-display font-bold text-primary">{s.total_remaining.toLocaleString()}</TableCell>
                    <TableCell><Badge className={st.color}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setDetailStudentId(s.student_id)} data-testid={`finance-manage-${s.student_id}`}>Gérer</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {detailStudentId && (
        <StudentFinanceDialog studentId={detailStudentId} onClose={() => setDetailStudentId(null)} onChanged={load} />
      )}
    </div>
  );
}

function NewPlanDialog({ open, setOpen, onCreated }) {
  const [students, setStudents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [customYear, setCustomYear] = useState(false);
  const [installments, setInstallments] = useState([{ label: "Tranche 1", amount: "", due_date: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users?role=etudiant").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/academic").then((r) => {
      const years = [...new Set(r.data.filter((a) => a.type === "promotion").map((a) => a.name))].sort().reverse();
      setAcademicYears(years);
      if (years.length === 0) setCustomYear(true);
    }).catch(() => {});
  }, [open]);

  const pickStudent = (id) => {
    setStudentId(id);
    const st = students.find((s) => s.id === id);
    if (st?.promotion) { setAcademicYear(st.promotion); setCustomYear(false); }
  };

  const addInstallment = () => setInstallments((s) => [...s, { label: `Tranche ${s.length + 1}`, amount: "", due_date: "" }]);
  const updateInstallment = (i, field, value) => setInstallments((s) => s.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));
  const removeInstallment = (i) => setInstallments((s) => s.filter((_, idx) => idx !== i));
  const total = installments.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

  const reset = () => { setStudentId(""); setAcademicYear(""); setCustomYear(false); setInstallments([{ label: "Tranche 1", amount: "", due_date: "" }]); };

  const save = async () => {
    if (!studentId || !academicYear.trim()) return toast.error("Choisissez un étudiant et une année académique.");
    if (installments.some((i) => !i.label || !i.amount || !i.due_date)) return toast.error("Complétez toutes les échéances (ou supprimez les lignes vides).");
    setSaving(true);
    try {
      const { data } = await api.post("/finance/plans", {
        student_id: studentId, academic_year: academicYear,
        installments: installments.map((i) => ({ label: i.label, amount: Number(i.amount), due_date: i.due_date })),
      });
      toast.success("Échéancier créé");
      setOpen(false); reset();
      onCreated(data.student_id);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="new-plan-btn"><Plus size={18} className="mr-2" /> Nouvel échéancier</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Créer un échéancier de scolarité</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Étudiant</Label>
            <Select value={studentId} onValueChange={pickStudent}>
              <SelectTrigger data-testid="new-plan-student"><SelectValue placeholder="Choisir un étudiant..." /></SelectTrigger>
              <SelectContent>
                {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} {s.filiere ? `(${s.filiere})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Année académique</Label>
            {customYear ? (
              <div className="flex gap-2">
                <Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="2026-2027" data-testid="new-plan-year" />
                {academicYears.length > 0 && <Button type="button" variant="outline" onClick={() => { setCustomYear(false); setAcademicYear(""); }}>Liste</Button>}
              </div>
            ) : (
              <Select value={academicYear} onValueChange={(v) => (v === "__custom" ? setCustomYear(true) : setAcademicYear(v))}>
                <SelectTrigger data-testid="new-plan-year"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {academicYears.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  <SelectItem value="__custom">Autre (saisie libre)...</SelectItem>
                </SelectContent>
              </Select>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Pré-remplie automatiquement d'après la promotion de l'étudiant sélectionné.</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <Label className="font-medium">Échéances</Label>
            <div className="mt-2 space-y-2">
              {installments.map((inst, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={inst.label} onChange={(e) => updateInstallment(i, "label", e.target.value)} placeholder="Libellé" className="flex-1" data-testid={`inst-label-${i}`} />
                  <Input type="number" value={inst.amount} onChange={(e) => updateInstallment(i, "amount", e.target.value)} placeholder="Montant" className="w-32" data-testid={`inst-amount-${i}`} />
                  <Input type="date" value={inst.due_date} onChange={(e) => updateInstallment(i, "due_date", e.target.value)} className="w-40" data-testid={`inst-date-${i}`} />
                  <Button size="icon" variant="ghost" type="button" onClick={() => removeInstallment(i)}><Trash size={16} className="text-accent" /></Button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" type="button" onClick={addInstallment} className="mt-2" data-testid="add-installment-btn"><Plus size={14} className="mr-1" /> Ajouter une échéance</Button>
            <p className="mt-2 text-xs font-medium text-primary">Total : {total.toLocaleString()}</p>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-new-plan-btn">{saving ? "Création..." : "Créer"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentFinanceDialog({ studentId, onClose, onChanged }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payMethod, setPayMethod] = useState("especes");
  const [payReference, setPayReference] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/finance/plans/${studentId}`).then((r) => setStatus(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [studentId]);

  const recordPayment = async () => {
    if (!payAmount || Number(payAmount) <= 0) return toast.error("Montant invalide.");
    setSaving(true);
    try {
      await api.post("/finance/payments", {
        student_id: studentId, amount: Number(payAmount), payment_date: payDate || null,
        method: payMethod, reference: payReference || null, notes: payNotes || null,
      });
      toast.success("Paiement enregistré");
      setPayOpen(false); setPayAmount(""); setPayDate(""); setPayReference(""); setPayNotes("");
      load(); onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Supprimer ce paiement ?")) return;
    try { await api.delete(`/finance/payments/${id}`); toast.success("Paiement supprimé"); load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deletePlan = async () => {
    if (!window.confirm("Supprimer tout l'échéancier et l'historique de paiement de cet étudiant ? Cette action est irréversible.")) return;
    try { await api.delete(`/finance/plans/${status.plan_id}`); toast.success("Échéancier supprimé"); onChanged(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const exportStatement = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/finance/plans/${studentId}/statement-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `releve-scolarite-${status.student.name}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const res = await api.get(`/finance/payments/${paymentId}/receipt-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `recu-paiement-${paymentId.slice(0, 8)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="finance-detail-dialog">
        {loading || !status ? (
          <>
            <DialogHeader><DialogTitle>Fiche financière</DialogTitle></DialogHeader>
            <p className="py-8 text-center text-sm text-muted-foreground">Chargement...</p>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>{status.student.name}</DialogTitle></DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-secondary p-4">
              <div className="text-sm text-muted-foreground">
                {status.student.filiere || "—"} {status.student.promotion ? `· ${status.student.promotion}` : ""} · {status.academic_year || "—"}
              </div>
              <div className="flex gap-4 text-sm">
                <span>Dû : <strong>{status.total_due.toLocaleString()}</strong></span>
                <span>Payé : <strong className="text-emerald-600">{status.total_paid.toLocaleString()}</strong></span>
                <span>Restant : <strong className={status.total_remaining > 0 ? "text-accent" : ""}>{status.total_remaining.toLocaleString()}</strong></span>
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-sm font-semibold">Échéancier</p>
              {status.installments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune échéance.</p>
              ) : (
                <div className="space-y-2">
                  {status.installments.map((inst) => (
                    <div key={inst.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                      <div>
                        <p className="font-medium">{inst.label}</p>
                        <p className="text-xs text-muted-foreground">Échéance : {inst.due_date}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{inst.paid.toLocaleString()} / {inst.amount.toLocaleString()}</span>
                        {inst.status === "payé" ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle size={12} className="mr-1" /> Payé</Badge>
                        ) : inst.status === "en retard" ? (
                          <Badge className="bg-accent hover:bg-accent"><WarningCircle size={12} className="mr-1" /> En retard</Badge>
                        ) : (
                          <Badge variant="secondary">À venir</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold">Historique des paiements</p>
                <Dialog open={payOpen} onOpenChange={setPayOpen}>
                  <DialogTrigger asChild><Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="add-payment-btn"><Plus size={14} className="mr-1" /> Enregistrer un paiement</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Montant</Label><Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" /></div>
                      <div><Label>Date (défaut : aujourd'hui)</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} /></div>
                      <div>
                        <Label>Moyen de paiement</Label>
                        <Select value={payMethod} onValueChange={setPayMethod}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(METHOD_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Référence (optionnel)</Label><Input value={payReference} onChange={(e) => setPayReference(e.target.value)} placeholder="N° de reçu, transaction..." /></div>
                      <div><Label>Notes (optionnel)</Label><Input value={payNotes} onChange={(e) => setPayNotes(e.target.value)} /></div>
                    </div>
                    <DialogFooter><Button onClick={recordPayment} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-payment-btn">{saving ? "Enregistrement..." : "Enregistrer"}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {status.payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun paiement enregistré.</p>
              ) : (
                <div className="space-y-2">
                  {status.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                      <div>
                        <p className="font-medium">{p.amount.toLocaleString()} <span className="font-normal text-muted-foreground">· {METHOD_LABEL[p.method] || p.method}</span></p>
                        <p className="text-xs text-muted-foreground">{p.payment_date} {p.reference ? `· ${p.reference}` : ""}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => downloadReceipt(p.id)} data-testid={`receipt-${p.id}`}><Receipt size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deletePayment(p.id)} data-testid={`del-payment-${p.id}`}><Trash size={16} className="text-accent" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="mt-4 flex-wrap gap-2">
              <Button variant="outline" onClick={deletePlan} className="border-accent text-accent hover:bg-accent/10" data-testid="delete-plan-btn">
                <Trash size={16} className="mr-2" /> Supprimer l'échéancier
              </Button>
              <Button onClick={exportStatement} disabled={exporting} className="bg-primary hover:bg-primary/90" data-testid="export-statement-btn">
                <DownloadSimple size={16} className="mr-2" /> {exporting ? "Génération..." : "Exporter le relevé"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
